/**
 * The author-written text that becomes a skill parameter's model-facing
 * description.
 *
 * Two vocabularies meet here and they are easy to confuse:
 *
 * - A WORKFLOW stores its agent-input parameters as JSON Schema, so the
 *   author's words live on `agentInputSchema[key].description`. The dashboard
 *   field says exactly what it is for: "Add a description of the property so the
 *   model knows what it is and how to use it".
 * - An ATTACHED SKILL stores a {@link ParameterSetup}, where the same words live
 *   on `prompt`.
 *
 * Attaching a workflow used to hardcode `prompt: ""` and discard the
 * description, so {@link computeAgentInputSchema} emitted `description: ""` and
 * the model was handed a parameter it knew nothing about. These helpers are the
 * single source for translating between the two spellings, shared by the
 * dashboard attach path, the backend attach path and the republish sync.
 */

import { type ParameterSetup } from "./workflow.types";

/** A single entry of a {@link ParameterSetup}. */
type ParameterSetupEntry = NonNullable<ParameterSetup[keyof ParameterSetup]>;

/**
 * True when a prompt carries no guidance for the model.
 *
 * Whitespace counts as blank: a prompt of `"   "` is indistinguishable from an
 * absent one at the provider, and treating it as authored content is what let
 * empty descriptions look deliberate.
 */
export function isBlankPrompt(prompt: unknown): boolean {
  return typeof prompt !== "string" || prompt.trim().length === 0;
}

/**
 * Read the author's description off a JSON-Schema parameter node.
 *
 * Returns `""` when the node is not an object, carries no `description`, or
 * carries a blank one — so the caller can test the result with
 * {@link isBlankPrompt} rather than juggling `undefined`.
 */
export function readSchemaDescription(schema: unknown): string {
  if (typeof schema !== "object" || schema === null || Array.isArray(schema)) return "";

  const description = (schema as { description?: unknown }).description;
  return isBlankPrompt(description) ? "" : (description as string).trim();
}

/**
 * Fill every blank prompt in a parameter setup from a reference setup, at every
 * depth.
 *
 * The republish sync keeps an operator's stored setup whenever the workflow's
 * shape still matches it, and derives a fresh `reference` from the workflow. A
 * prompt the operator WROTE is a customisation and is preserved. A BLANK prompt
 * is the absence of one — it is exactly what reaches the model as a parameter
 * with no description — so it takes the workflow author's own words instead.
 *
 * The recursion is the whole point. Seeding only the top level leaves every
 * field of an object-typed parameter blank, which is the majority of the
 * interesting ones: an `address` parameter the model can see but whose `city`
 * and `postcode` it is told nothing about.
 *
 * Pure — the input is never mutated, so a shared placeholder cannot be
 * contaminated by one config and then reused for the next.
 */
export function seedBlankPrompts(
  setup: ParameterSetupEntry,
  reference: ParameterSetupEntry | undefined
): ParameterSetupEntry {
  if (!reference) return setup;

  const seeded: ParameterSetupEntry = { ...setup };

  if (isBlankPrompt(seeded.prompt) && !isBlankPrompt(reference.prompt)) {
    seeded.prompt = reference.prompt;
  }

  if (seeded.properties && reference.properties) {
    const properties: ParameterSetup = {};
    for (const key of Object.keys(seeded.properties)) {
      const child = seeded.properties[key];
      if (!child) continue;
      properties[key] = seedBlankPrompts(child, reference.properties[key]);
    }
    seeded.properties = properties;
  }

  return seeded;
}
